// =================================================================================================
// >>> **HER MEMBERSHIP LANDER (2026-08-27). THE PAGE THAT SELLS.** <<<
//
// **HER VERDICT ON WHAT THIS REPLACES: *"it reads as a money grab."*** The page that stood here was
// her artwork with three invisible tap targets positioned over it as percentages — a price list in a
// picture frame. It said what things cost and never once said what the house IS.
//
// Her brief, verbatim in intent: a true landing page that SELLS. **WHITE BACKGROUND — a deliberate
// departure, the one bright page in the house** — with plum and black as accents and GOLD CHROME for
// headlines and highlights, no brown anywhere. A headline that sells the truth. What-you-get per tier
// in beautiful detail. Reviews prominent, in gold, woven through rather than stuffed in image boxes.
// Marketing sections on what they are missing. Join buttons tier-aware per the standing laws.
//
// -------------------------------------------------------------------------------------------------
// >>> **THE ONE LAW THIS PAGE MOST HAD TO KEEP, AND IT IS KEPT: NOTHING HERE DECIDES WHO GETS WHAT.**
//
// `MembershipPage`'s own comment names the defect: *"a second copy of that rule on the client is
// exactly the bug that made the free layer raise a box on her own birth chart."* So every feature
// line, every tier name, every price, every "Everything in Newbie" and every turns count on this page
// comes through `valueStacks` off the SERVER's catalogue — the same composer the old value stack
// read. **A prettier page is not a licence to re-type the ladder.**
//
// What IS written here is POSITIONING — prose about the house as a whole — and that is a different
// fact from what a tier includes (R18). Every room it names is read off the real FEATURE roster in
// `server/tiers.js` rather than imagined, because a lander advertising a room this house does not
// have is the anti-fabrication law broken on the one surface that asks members for money.
//
// >>> **AND THE FREE LAYER IS DRAWN ONCE INSTEAD OF THREE TIMES, WHICH IS PRESENTATION AND NOT A
// SECOND RULE.** <<< `valueStackFor` folds the free layer into EVERY tier's stack, which is right for
// a stack read one tier at a time and reads as padding when three cards sit side by side. The lines
// are the same lines from the same composer; only where they are drawn is this file's decision.
//
// **HOOKLESS, SO A GATE CALLS IT** (R7/R8) and reads the tree back — the instrument that can tell a
// page drawing her real ladder from one drawing a hand-typed imitation of it.
// =================================================================================================
import {
  MessageCircle, Orbit, Sun, Layers, Hash, Aperture, Globe, Moon, Hand, Heart,
  Stamp, FlaskConical, BookOpen, GraduationCap, Users, Radio, PenLine, DoorOpen,
} from "lucide-react";
// HER LOGO. The round 12th House mark the menu already wears -- the SAME asset rather than a
// second file, so a re-cut of hers moves every surface at once (R28/R29).
import menuLogoUrl from "../assets/menu-logo.webp";
import { t } from "../strings.js";
// F: the ONE law that prices her real rate — the same one that set her Stripe coupon, so this card
// and her invoice cannot drift into two answers about her money (R28/R29).
import { memberRateLine } from "../memberRate.js";
import { DEFAULT_LANGUAGE } from "../language.js";
import { tierControlState, tierControlActs, tierControlLabel, tierControlNote } from "../membershipPage.js";
import type { ValueCatalog } from "../lib/tier-catalog";

type StackLine = { key: string; label: string };
type StackGroup = { kind: string; label: string; lines: StackLine[] };
type Stack = { tier: string; inherits: string; turns: number; groups: StackGroup[] };

// The copy lives in `strings.js` with its Spanish beside it, which is this house's standing rule
// for anything a member reads.
//
// >>> **EVERY KEY IS SPELLED OUT AND NONE IS BUILT FROM A TEMPLATE, WHICH IS ITEM 14b's OWN LAW
// WORKING.** <<< An earlier draft composed its keys at runtime. It rendered correctly and the gate
// that reads *every catalog row is actually READ by a surface* went RED about every row —
// **rightly**: a key assembled at runtime is invisible to that reader, to a grep, and to the next
// person deleting a row they believe nothing uses.
//
// ===============================================================================================
// >>> **HER ROUND-2 VERDICT ON WHAT STOOD HERE: BORING.** <<<
//
// Six evocative cards that grouped her practice into themes. Her order: *"every offering listed
// INDIVIDUALLY ... each with a line written to BENEFIT, not description: what it does FOR them, in
// her register, real-life outcomes, zero feature-speak ... composed as beautiful cards/tiles with
// the gold chrome, icons/visual rhythm, never a gray list."*
//
// **EIGHTEEN OFFERINGS, EACH WITH ITS OWN ICON**, because her clause asks for visual rhythm and a
// grid of eighteen text blocks is the gray list she named. The icons are `lucide-react`, which
// this house already ships — a second icon set for one page would be weight on the surface her
// advertising lands on.
// ===============================================================================================
const OFFERINGS = [
  { key: "askLala", Icon: MessageCircle, title: "membership.lander.o.askLala.title", body: "membership.lander.o.askLala.body" },
  { key: "chart", Icon: Orbit, title: "membership.lander.o.chart.title", body: "membership.lander.o.chart.body" },
  { key: "daily", Icon: Sun, title: "membership.lander.o.daily.title", body: "membership.lander.o.daily.body" },
  { key: "tarot", Icon: Layers, title: "membership.lander.o.tarot.title", body: "membership.lander.o.tarot.body" },
  { key: "numerology", Icon: Hash, title: "membership.lander.o.numerology.title", body: "membership.lander.o.numerology.body" },
  { key: "humanDesign", Icon: Aperture, title: "membership.lander.o.humanDesign.title", body: "membership.lander.o.humanDesign.body" },
  { key: "acg", Icon: Globe, title: "membership.lander.o.acg.title", body: "membership.lander.o.acg.body" },
  { key: "dreams", Icon: Moon, title: "membership.lander.o.dreams.title", body: "membership.lander.o.dreams.body" },
  { key: "palm", Icon: Hand, title: "membership.lander.o.palm.title", body: "membership.lander.o.palm.body" },
  { key: "compat", Icon: Heart, title: "membership.lander.o.compat.title", body: "membership.lander.o.compat.body" },
  { key: "seals", Icon: Stamp, title: "membership.lander.o.seals.title", body: "membership.lander.o.seals.body" },
  { key: "magic", Icon: FlaskConical, title: "membership.lander.o.magic.title", body: "membership.lander.o.magic.body" },
  { key: "library", Icon: BookOpen, title: "membership.lander.o.library.title", body: "membership.lander.o.library.body" },
  { key: "masterclasses", Icon: GraduationCap, title: "membership.lander.o.masterclasses.title", body: "membership.lander.o.masterclasses.body" },
  { key: "community", Icon: Users, title: "membership.lander.o.community.title", body: "membership.lander.o.community.body" },
  { key: "lives", Icon: Radio, title: "membership.lander.o.lives.title", body: "membership.lander.o.lives.body" },
  { key: "journal", Icon: PenLine, title: "membership.lander.o.journal.title", body: "membership.lander.o.journal.body" },
  { key: "myRoom", Icon: DoorOpen, title: "membership.lander.o.myRoom.title", body: "membership.lander.o.myRoom.body" },
] as const;

export function MembershipLander({
  language = DEFAULT_LANGUAGE,
  stacks,
  catalog,
  ladderTier,
  // F: her REAL monthly rate, when a standing credit makes it differ from the tier's sticker.
  // `null` for almost everybody, and the card draws nothing at all in that case.
  rate = null,
  busy = "",
  quotes = [],
  onJoin,
}: {
  language?: string;
  stacks?: Stack[] | null;
  catalog?: ValueCatalog | null;
  ladderTier?: string;
  rate?: { tier: string; rate: number; credit: number; heldLabel?: string } | null;
  busy?: string;
  quotes?: { text: string; name: string }[];
  // ITEM 11(b): the STATE rides with the tier, because a move down presses a different door and
  // the card is the only place that knows which it is.
  onJoin: (tier: string, state?: string) => void;
}) {
  const ladder = catalog?.ladder || [];
  const rowFor = (tier: string) => ladder.find((r) => String(r?.tier || "").toLowerCase() === tier);

  // >>> **THE FREE LAYER, ONCE.** <<< Taken off the FIRST stack that carries one rather than from a
  // second read of the catalogue: one composer, one answer (R28/R29).
  const freeGroup = (stacks || []).map((s) => s.groups.find((g) => g.kind === "free")).find(Boolean) || null;

  // A pool smaller than the places we would like to put a quote is its own answer (R18): each slot
  // below asks for one BY INDEX and draws nothing when the pool has run out, because an empty space
  // is a page with fewer quotes and a repeated quote is a page that looks broken.
  const quoteAt = (i: number) => (i < quotes.length ? quotes[i] : null);
  const byline = (name: string) => <figcaption className="lander__quotename">@{name}</figcaption>;

  return (
    <div className="lander">

      {/* ---- HERO ------------------------------------------------------------------------- */}
      <header className="lander__hero">
        {/* >>> **HER LOGO, HER ROUND-2 NOTE 1: it was absent entirely.** <<< The round mark the menu
            already wears, at the top of the page, sized with intention rather than tucked in a
            corner. `alt=""` because the eyebrow directly under it says "The 12th House" in words —
            a screen reader hearing the name twice in two elements is noise, not access. */}
        <img className="lander__logo" src={menuLogoUrl} alt="" width={132} height={132} decoding="async" />
        <p className="lander__eyebrow">{t(language, "membership.lander.eyebrow")}</p>
        {/* >>> THE HERO LINE IS DISPLAY TYPE AND DELIBERATELY NOT AN `<h1>` (R43). <<< R43 gave this
            page the one heading its outline was missing, DERIVED from `PAGE_TITLES` so the tab and
            the outline can never call it two things, and `r43-mutations` catches a second `<h1>` by
            name. A screen reader is told what this page IS by that heading; this is the sentence it
            SELLS with, and the lede under it carries the same message in flowing text. So the
            outline reads h1 Membership -> h2 sections -> h3 rooms, which is clean either way. */}
        <p className="lander__h1">
          <span className="lander__h1a">{t(language, "membership.lander.h1a")}</span>
          <span className="lander__h1b">{t(language, "membership.lander.h1b")}</span>
        </p>
        <p className="lander__lede">{t(language, "membership.lander.lede")}</p>
        {/* >>> **THE CONTROL IS DRAWN ONLY WHERE IT HAS SOMEWHERE TO GO (R35).** <<< It jumps to the
            tier section, and that section is not rendered when the catalogue read has not landed or
            was refused. Drawn unconditionally it would be a button that answers a tap by doing
            nothing — on the one page that asks her members for money, which is worse than no button
            at all. Absent, the page is prose and a way out, which is honest about what it knows. */}
        {stacks && stacks.length
          ? <a className="lander__cta" href="#membership-tiers">{t(language, "membership.lander.heroCta")}</a>
          : null}
        <p className="lander__heronote">{t(language, "membership.lander.heroNote")}</p>
      </header>

      {/* ---- A MEMBER'S VOICE, HIGH ON THE PAGE ------------------------------------------- */}
      {/* HER ORDER, 2026-10-02: the tiers come right under See what is inside, then her Thank you, Lala words, then everything you get, one by one. */}
      {/* ---- THE TIERS -------------------------------------------------------------------- */}
      {/* >>> A LADDER THIS HOUSE COULD NOT READ IS NOT AN EMPTY SHOP (R73/R87). <<< A refused or
          unlanded catalogue draws NO tier section at all, rather than three headings over nothing —
          which on the page that asks for money would read as *this tier includes nothing*. */}
      {stacks && stacks.length ? (
        <section className="lander__tiers" id="membership-tiers" aria-labelledby="lander-tiers">
          <h2 className="lander__h2" id="lander-tiers">{t(language, "membership.lander.tiersTitle")}</h2>
          <p className="lander__sectionlede">{t(language, "membership.lander.tiersLede")}</p>

          <div className="lander__cards">
            {stacks.map((s, i) => {
              const row = rowFor(s.tier);
              const state = tierControlState(s.tier, ladderTier);
              const label = tierControlLabel(state);
              const acts = tierControlActs(state);
              // UPGRADE-NOTE-1 — the move UP explains itself, because the move DOWN already does.
              // Four of the five members at the desk's front door on 2026-09-04 had written in about
              // this one thing, and the law answers "" for every card that needs no note and for a
              // price that has not arrived, so nothing here decides when it is drawn.
              const note = tierControlNote(state, { priceText: row?.priceText || "" });
              // The middle rung is the one the house recommends, and it is chosen by POSITION in the
              // server's own ladder rather than by naming "apprentice" here — a fourth tier moves the
              // emphasis by existing (R11/R31).
              const featured = stacks.length === 3 && i === 1;
              const own = s.groups.find((g) => g.kind === "tier");
              return (
                <article
                  key={s.tier}
                  data-tier-card={s.tier}
                  data-tier-state={state}
                  className={`lander__card${featured ? " lander__card--featured" : ""}${state === "current" ? " lander__card--current" : ""}`}
                >
                  {state === "current" ? <p className="lander__cardbadge">{label}</p> : null}
                  {/* The NAME and the PRICE are the server's, never spelled here (R28/R29 — W1-5
                      measured what two spellings of a price cost this house once already). */}
                  <h3 className="lander__cardname">{row?.label || ""}</h3>
                  <p className="lander__cardprice">
                    <span className="lander__cardamount">{row?.priceText || ""}</span>
                    <span className="lander__cardperiod">{t(language, "membership.lander.perMonth")}</span>
                  </p>
                  {/* >>> **HER REAL RATE, UNDER THE STICKER, ON HER CARD ALONE (2026-09-05).** <<<
                      `chesson.cinchonia@yahoo.com`, two and a half hours after being told her rate
                      was $80: *"But in my thing it shows 149."* She was reading this card. The
                      sticker above STAYS — it is what the tier costs anyone who does not hold what
                      she holds — but it is no longer the last word on her own screen. The sentence
                      names the CREDIT as well as the number, so it explains itself instead of
                      looking like a mistake that might be taken back.
                      **It draws only on the tier she is actually paying for, and only when a credit
                      makes the two differ**; `memberRate` answers `null` for everybody else, so no
                      other member sees a line about a discount she does not have. */}
                  {rate && rate.tier === s.tier ? (
                    <p className="lander__cardrate" data-member-rate={s.tier}>{memberRateLine(rate)}</p>
                  ) : null}
                  {row?.tagline ? <p className="lander__cardtagline">{row.tagline}</p> : null}

                  <button
                    type="button"
                    className="lander__join"
                    data-tier-control={s.tier}
                    aria-label={`${row?.label || s.tier}: ${label || "membership"}`}
                    disabled={!acts || busy === s.tier}
                    onClick={() => onJoin(s.tier, state)}
                  >
                    {label || " "}
                  </button>

                  {/* UPGRADE-NOTE-1 — under the button, because it is about what pressing it does.
                      The PRICE is the server's, handed to the row rather than spelled here (R28/R29). */}
                  {note ? <p className="lander__cardnote">{t(language, note, { price: row?.priceText || "" })}</p> : null}

                  {/* The inheritance is a LINE and never a heading: it is a promise about the tier
                      below rather than a section of this one's list. */}
                  {s.inherits ? <p className="lander__cardinherits">{s.inherits}</p> : null}
                  {/* A tier with no turns draws NO turns line — "0 Ask Lala turns a day" on the $39
                      card is a promise of nothing dressed as a feature (R18). */}
                  {s.turns > 0
                    ? <p className="lander__cardturns">{t(language, "membership.valueTurns", { count: s.turns })}</p>
                    : null}
                  {own && own.lines.length ? (
                    <ul className="lander__cardlist">
                      {own.lines.map((l) => (
                        <li className="lander__cardline" key={l.key || l.label}>{l.label}</li>
                      ))}
                    </ul>
                  ) : null}
                </article>
              );
            })}
          </div>

          {/* The free layer, drawn ONCE under the three, from the same composer. */}
          {freeGroup && freeGroup.lines.length ? (
            <div className="lander__free" data-lander-free="1">
              {freeGroup.label ? <p className="lander__freelabel">{freeGroup.label}</p> : null}
              <ul className="lander__freelist">
                {freeGroup.lines.map((l) => (
                  <li className="lander__freeline" key={l.key || l.label}>{l.label}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>
      ) : null}

      {quoteAt(0) ? (
        <figure className="lander__pull" data-lander-quote="0">
          <blockquote className="lander__pullquote">{quoteAt(0)?.text}</blockquote>
          {byline(quoteAt(0)?.name || "")}
        </figure>
      ) : null}

      {/* ---- WHAT LIVES IN THE HOUSE ------------------------------------------------------ */}
      <section className="lander__section" aria-labelledby="lander-inside">
        <h2 className="lander__h2" id="lander-inside">{t(language, "membership.lander.insideTitle")}</h2>
        <p className="lander__sectionlede">{t(language, "membership.lander.insideLede")}</p>
        <div className="lander__offers">
          {OFFERINGS.map((o) => (
            <article className="lander__offer" key={o.key} data-lander-offer={o.key}>
              <span className="lander__offericon" aria-hidden="true"><o.Icon size={22} strokeWidth={1.5} /></span>
              <h3 className="lander__offertitle">{t(language, o.title)}</h3>
              <p className="lander__offerbody">{t(language, o.body)}</p>
            </article>
          ))}
        </div>
      </section>

      {quoteAt(1) ? (
        <figure className="lander__pull" data-lander-quote="1">
          <blockquote className="lander__pullquote">{quoteAt(1)?.text}</blockquote>
          {byline(quoteAt(1)?.name || "")}
        </figure>
      ) : null}

      {/* ---- THEIR OWN WORDS -------------------------------------------------------------- */}
      {quotes.length > 2 ? (
        <section className="lander__section" aria-labelledby="lander-reviews">
          <h2 className="lander__h2" id="lander-reviews">{t(language, "membership.lander.reviewsTitle")}</h2>
          <div className="lander__quotes">
            {quotes.slice(2, 8).map((q, i) => (
              <figure className="lander__quote" key={i} data-lander-quote={i + 2}>
                <blockquote className="lander__quotetext">{q.text}</blockquote>
                {byline(q.name)}
              </figure>
            ))}
          </div>
        </section>
      ) : null}

      {/* ---- THE CLOSE -------------------------------------------------------------------- */}
      {/* 1e: her heading and its paragraph are gone; the one button glides back up to the tiers. */}
      <section className="lander__close">
        {stacks && stacks.length
          ? <a className="lander__cta" href="#membership-tiers" data-lander-close onClick={(e) => { const to = document.getElementById("membership-tiers"); if (!to) return; e.preventDefault(); to.scrollIntoView({ behavior: "smooth", block: "start" }); }}>{t(language, "membership.lander.closeCta")}</a>
          : null}
      </section>
    </div>
  );
}
